// Canvas interactivo donde el alumno puede pintar/encerrar/escribir sobre la
// imagen base de la pregunta. La respuesta se exporta como dataURL PNG.
//
// Herramientas:
//   - brush-*  : trazos a mano alzada (colores)
//   - underline: trazo grueso amarillo (subrayador)
//   - circle   : trazo fino oscuro (encerrar)
//   - text     : coloca un cuadro de texto en cualquier punto del gráfico
//   - eraser   : borrador
//
// La herramienta "text" funciona por CLIC, no por arrastre: al hacer clic
// aparece un input flotante posicionado donde el alumno tocó; al pulsar
// Enter (o perder el foco) el texto se "estampa" sobre el canvas y queda
// guardado en el dataURL exportado.
import { useEffect, useRef, useState } from "react";
import { Eraser, Undo2, Type, Highlighter, Circle as CircleIcon } from "lucide-react";

const TOOL_DEFS = {
  "brush-red":    { color: "#EF4444", width: 5,  label: "Rojo" },
  "brush-blue":   { color: "#3B82F6", width: 5,  label: "Azul" },
  "brush-green":  { color: "#10B981", width: 5,  label: "Verde" },
  "brush-yellow": { color: "#F59E0B", width: 5,  label: "Amarillo" },
  "brush-pink":   { color: "#EC4899", width: 5,  label: "Rosa" },
  // Resaltador: CAJA amarilla translúcida (arrastrar para definir el recuadro).
  "underline":    { color: "#FBBF24", width: 0, label: "Resaltador", alpha: 0.3, composite: "multiply", shape: "rect" },
  // Encerrar: tipo "shape" — al arrastrar define el bounding-box del círculo/elipse.
  "circle":       { color: "#1F2547", width: 4,  label: "Encerrar", shape: "ellipse" },
  "text":         { color: "#1F2547", width: 0,  label: "Texto",    shape: "text" },
  "eraser":       { color: "#FFFFFF", width: 22, label: "Borrador", mode: "erase" },
};

const TEXT_COLORS = ["#1F2547", "#EF4444", "#2C49A0", "#3FA84D", "#A43790", "#F59E0B"];
const TEXT_SIZES = [
  { id: "sm", px: 16, label: "S" },
  { id: "md", px: 22, label: "M" },
  { id: "lg", px: 32, label: "L" },
];

export default function ImageInteractiveCanvas({
  imageUrl, drawingTools = ["brush-red", "eraser"], value, onChange,
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const inputRef = useRef(null);
  // Snapshot que se restaura mientras se "previsualiza" una forma (circle).
  const shapeSnapshotRef = useRef(null);
  const shapeStartRef = useRef(null);
  const [tool, setTool] = useState(drawingTools[0] || "brush-red");
  const [drawing, setDrawing] = useState(false);
  const [history, setHistory] = useState([]);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [size, setSize] = useState({ w: 600, h: 400 });

  // Estado del input flotante de texto: { x, y, value, color, fontPx }
  const [textBox, setTextBox] = useState(null);
  const [textColor, setTextColor] = useState(TEXT_COLORS[0]);
  const [textSize, setTextSize] = useState("md");

  useEffect(() => {
    if (!imageUrl) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const w = Math.min(600, img.naturalWidth);
      const ratio = img.naturalHeight / img.naturalWidth;
      setSize({ w, h: w * ratio });
      imgRef.current = img;
      setImgLoaded(true);
    };
    img.onerror = () => setImgLoaded(false);
    img.src = imageUrl;
  }, [imageUrl]);

  useEffect(() => {
    if (!imgLoaded || !canvasRef.current) return;
    const c = canvasRef.current;
    c.width = size.w; c.height = size.h;
    const ctx = c.getContext("2d");
    ctx.drawImage(imgRef.current, 0, 0, size.w, size.h);
    if (value && typeof value === "string" && value.startsWith("data:")) {
      const overlay = new Image();
      overlay.onload = () => ctx.drawImage(overlay, 0, 0, size.w, size.h);
      overlay.src = value;
    }
  }, [imgLoaded, size, value]);

  // Si se cambia a otra herramienta mientras hay un cuadro abierto, lo confirmamos.
  useEffect(() => {
    if (textBox && tool !== "text") commitText();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const sx = canvasRef.current.width / rect.width;
    const sy = canvasRef.current.height / rect.height;
    const cx = e.touches?.[0]?.clientX ?? e.clientX;
    const cy = e.touches?.[0]?.clientY ?? e.clientY;
    const x = cx - rect.left;
    const y = cy - rect.top;
    return {
      x: x * sx, y: y * sy,                      // coords del canvas (resolución real)
      screenX: x, screenY: y,                     // coords del overlay HTML (display)
    };
  };

  // ---------- Modo texto ----------
  const fontPx = (TEXT_SIZES.find((s) => s.id === textSize) || TEXT_SIZES[1]).px;

  const openTextBox = (e) => {
    e.preventDefault();
    if (textBox) commitText();
    const { x, y, screenX, screenY } = getPos(e);
    setTextBox({ x, y, screenX, screenY, value: "" });
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const commitText = () => {
    const tb = textBox;
    if (!tb) return;
    if (!tb.value || !tb.value.trim()) { setTextBox(null); return; }
    const ctx = canvasRef.current.getContext("2d");
    setHistory((h) => [...h, ctx.getImageData(0, 0, size.w, size.h)]);
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = textColor;
    ctx.font = `${fontPx}px "Nunito", "Quicksand", sans-serif`;
    ctx.textBaseline = "top";
    // Multilinea: respeta saltos de línea explícitos
    const lines = tb.value.split("\n");
    lines.forEach((ln, i) => ctx.fillText(ln, tb.x, tb.y + i * (fontPx * 1.2)));
    setTextBox(null);
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  // ---------- Trazos (brush/erase/shape/etc.) ----------
  const start = (e) => {
    if (tool === "text") { openTextBox(e); return; }
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    setHistory((h) => [...h, ctx.getImageData(0, 0, size.w, size.h)]);
    setDrawing(true);
    const def = TOOL_DEFS[tool] || TOOL_DEFS["brush-red"];
    if (def.shape === "ellipse" || def.shape === "rect") {
      // Guardamos snapshot del lienzo actual para poder restaurarlo durante el drag.
      shapeSnapshotRef.current = ctx.getImageData(0, 0, size.w, size.h);
      shapeStartRef.current = { x, y };
      return;
    }
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (tool === "text" || !drawing) return;
    e.preventDefault();
    const def = TOOL_DEFS[tool] || TOOL_DEFS["brush-red"];
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);

    // -------- Modo forma: círculo / elipse --------
    if (def.shape === "ellipse") {
      if (!shapeSnapshotRef.current || !shapeStartRef.current) return;
      // Restaurar snapshot y dibujar la elipse temporal
      ctx.putImageData(shapeSnapshotRef.current, 0, 0);
      const { x: sx, y: sy } = shapeStartRef.current;
      const cx = (sx + x) / 2;
      const cy = (sy + y) / 2;
      const rx = Math.abs(x - sx) / 2;
      const ry = Math.abs(y - sy) / 2;
      ctx.save();
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = def.color;
      ctx.lineWidth = def.width;
      ctx.globalCompositeOperation = "source-over";
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
      return;
    }

    // -------- Modo forma: caja resaltadora (rectángulo translúcido) --------
    if (def.shape === "rect") {
      if (!shapeSnapshotRef.current || !shapeStartRef.current) return;
      ctx.putImageData(shapeSnapshotRef.current, 0, 0);
      const { x: sx, y: sy } = shapeStartRef.current;
      const rx = Math.min(sx, x);
      const ry = Math.min(sy, y);
      const rw = Math.abs(x - sx);
      const rh = Math.abs(y - sy);
      ctx.save();
      ctx.globalAlpha = def.alpha != null ? def.alpha : 0.3;
      ctx.globalCompositeOperation = def.composite || "multiply";
      ctx.fillStyle = def.color;
      ctx.fillRect(rx, ry, rw, rh);
      ctx.restore();
      return;
    }

    // -------- Modo trazo libre (brush / eraser) --------
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = def.color;
    ctx.lineWidth = def.width;
    ctx.globalAlpha = def.alpha != null ? def.alpha : 1;
    ctx.globalCompositeOperation = def.mode === "erase"
      ? "destination-out"
      : (def.composite || "source-over");
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
  };

  const end = (e) => {
    if (tool === "text" || !drawing) return;
    e.preventDefault();
    setDrawing(false);
    const ctx = canvasRef.current.getContext("2d");
    const def = TOOL_DEFS[tool] || TOOL_DEFS["brush-red"];
    if (!def.shape) {
      ctx.closePath();
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
    shapeSnapshotRef.current = null;
    shapeStartRef.current = null;
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const undo = () => {
    const last = history[history.length - 1];
    if (!last) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.putImageData(last, 0, 0);
    setHistory(history.slice(0, -1));
    setTextBox(null);
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      commitText();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setTextBox(null);
    }
  };

  // Escala canvas-real → display, para posicionar el input overlay en píxeles del DOM
  const displayScale = (() => {
    if (!canvasRef.current) return 1;
    const rect = canvasRef.current.getBoundingClientRect();
    return rect.width / (canvasRef.current.width || 1);
  })();

  return (
    <div className="space-y-2" data-testid="image-interactive-canvas">
      <div className="flex flex-wrap gap-2 items-center">
        {drawingTools.map((t) => {
          const def = TOOL_DEFS[t];
          if (!def) return null;
          const isText = t === "text";
          return (
            <button key={t} type="button" onClick={() => setTool(t)}
              data-testid={`canvas-tool-${t}`}
              className={`px-2 py-1.5 rounded-lg border-2 text-[11px] font-bold inline-flex items-center gap-1 ${tool === t ? "border-[#1F2547] bg-[#1F2547] text-white" : "border-[#E1E5F0] text-[#4C5478]"}`}>
              {isText ? <Type size={11} />
                : t === "eraser" ? <Eraser size={11} />
                : t === "underline" ? <Highlighter size={11} style={{ color: def.color }} />
                : t === "circle" ? <CircleIcon size={11} style={{ color: def.color }} strokeWidth={2.5} />
                : (
                  <span className="w-3 h-3 rounded-full border border-white/50" style={{ background: def.color }} />
                )}
              {def.label || t}
            </button>
          );
        })}
        <button onClick={undo} data-testid="canvas-undo"
          className="px-2 py-1.5 rounded-lg border-2 border-[#E1E5F0] text-[11px] font-bold text-[#4C5478] inline-flex items-center gap-1">
          <Undo2 size={11} /> Deshacer
        </button>
      </div>

      {/* Sub-toolbar del modo texto: color + tamaño */}
      {tool === "text" && drawingTools.includes("text") && (
        <div className="flex flex-wrap gap-2 items-center bg-[#FFF7DA] border-2 border-[#FFCC28] rounded-lg px-2 py-1.5" data-testid="canvas-text-subbar">
          <span className="text-[10px] uppercase tracking-wider font-extrabold text-[#9C7A18]">Texto</span>
          <div className="flex gap-1 items-center">
            {TEXT_COLORS.map((c) => (
              <button key={c} type="button" onClick={() => setTextColor(c)}
                data-testid={`canvas-text-color-${c.replace('#','')}`}
                style={{ background: c }}
                className={`w-5 h-5 rounded-full border-2 ${textColor === c ? "border-[#1F2547] ring-2 ring-offset-1 ring-[#1F2547]" : "border-white"}`} />
            ))}
          </div>
          <div className="flex gap-1">
            {TEXT_SIZES.map((s) => (
              <button key={s.id} type="button" onClick={() => setTextSize(s.id)}
                data-testid={`canvas-text-size-${s.id}`}
                className={`px-2 py-0.5 rounded border-2 text-[10px] font-extrabold ${textSize === s.id ? "border-[#1F2547] bg-[#1F2547] text-white" : "border-[#E1E5F0] text-[#4C5478]"}`}>
                {s.label}
              </button>
            ))}
          </div>
          <span className="text-[10px] text-[#9C7A18]">Toca la imagen para colocar el cuadro · Enter para confirmar · Esc para cancelar</span>
        </div>
      )}

      <div ref={wrapRef} className="relative inline-block max-w-full">
        <canvas
          ref={canvasRef}
          className="touch-none border-2 border-[#E1E5F0] rounded-xl bg-white max-w-full"
          style={{ width: "100%", maxWidth: size.w, height: "auto", cursor: tool === "text" ? "text" : "crosshair" }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />

        {textBox && (
          <div
            className="absolute"
            style={{ left: textBox.screenX, top: textBox.screenY, transform: "translate(0, -2px)" }}
            data-testid="canvas-text-box"
          >
            <textarea
              ref={inputRef}
              value={textBox.value}
              onChange={(e) => setTextBox({ ...textBox, value: e.target.value })}
              onBlur={commitText}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Escribe…"
              className="bg-white/95 border-2 border-[#A43790] rounded-md outline-none px-2 py-1 shadow-lg resize-none font-bold leading-tight"
              style={{
                color: textColor,
                fontSize: `${fontPx * displayScale}px`,
                fontFamily: '"Nunito","Quicksand",sans-serif',
                minWidth: 80,
                minHeight: `${fontPx * displayScale * 1.4}px`,
              }}
            />
          </div>
        )}
      </div>

      {tool === "text" && !textBox && (
        <p className="text-[11px] text-[#6E7591]">💡 Haz clic en cualquier parte de la imagen para colocar un cuadro de texto.</p>
      )}
    </div>
  );
}
